// Admin-tunable order settings. Right now this only exposes the tracking-number
// prefix (e.g. "VAZ-2026") so the admin can rebrand or roll the year without
// touching code. Stored as a single Firestore document at `settings/orders`
// so it can be cached client-side and edited from the Admin Orders page.

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";

export const DEFAULT_TRACKING_PREFIX = "VAZ-2026";
// 6 alphanumeric characters appended after the prefix (e.g. "03Y950").
const RANDOM_LENGTH = 6;

let _cache = null;
let _cachePromise = null;

/**
 * Returns the current order-settings document, creating one with sensible
 * defaults if it doesn't exist yet. Cached for the lifetime of the page so
 * repeated calls (e.g. inside listAll loops) don't hit Firestore each time.
 */
export async function getOrderSettings({ force = false } = {}) {
  if (_cache && !force) return _cache;
  if (_cachePromise && !force) return _cachePromise;
  _cachePromise = (async () => {
    try {
      const ref = doc(db, "settings", "orders");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        _cache = { tracking_prefix: DEFAULT_TRACKING_PREFIX, ...snap.data() };
      } else {
        _cache = { tracking_prefix: DEFAULT_TRACKING_PREFIX };
      }
      return _cache;
    } catch (_e) {
      _cache = { tracking_prefix: DEFAULT_TRACKING_PREFIX };
      return _cache;
    } finally {
      _cachePromise = null;
    }
  })();
  return _cachePromise;
}

/** Persist the tracking-number prefix and invalidate the cache. */
export async function setTrackingPrefix(prefix) {
  // Sanitize: keep letters / digits / dashes only, max 24 chars, strip
  // trailing dashes — we always append "-XXXXXX" after this value.
  const cleaned = String(prefix || "")
    .toUpperCase()
    .replace(/[^A-Z0-9\-]/g, "")
    .replace(/-+$/, "")
    .slice(0, 24) || DEFAULT_TRACKING_PREFIX;
  const ref = doc(db, "settings", "orders");
  await setDoc(
    ref,
    { tracking_prefix: cleaned, updated_at: serverTimestamp() },
    { merge: true }
  );
  _cache = { ...(_cache || {}), tracking_prefix: cleaned };
  return cleaned;
}

/**
 * Generate a new tracking number using the configured prefix. Pure browser
 * randomness (the previous lib/api.js implementation), kept identical so old
 * numbers remain valid lookups — only the prefix becomes dynamic.
 */
export function generateTrackingNo(prefix) {
  const p = prefix || DEFAULT_TRACKING_PREFIX;
  const rand = Math.random()
    .toString(36)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, RANDOM_LENGTH)
    .padEnd(RANDOM_LENGTH, "X");
  return `${p}-${rand}`;
}
